import React, { useEffect, useRef } from "react";
import { profileData } from "@/data/profile";
import BlurText from "./BlurText";

export default function About() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const ranRef = useRef(false); // guard to prevent double init (dev StrictMode)

  useEffect(() => {
    // Run only in browser
    if (typeof window === "undefined") return;

    // Respect reduced-motion preference
    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      // Optionally set final visible state using simple style changes, or just do nothing.
      return;
    }

    // Prevent double initialization in Strict Mode during dev
    if (ranRef.current) return;
    ranRef.current = true;

    let ctx: any = null;
    let ScrollTrigger: any = null;
    let gsap: any = null;
    let cancelled = false;

    (async () => {
      // dynamic imports so nothing runs on server
      const gsapModule = await import("gsap");
      const scrollTriggerModule = await import("gsap/ScrollTrigger");

      if (cancelled) return;

      // normalize imports (supports CJS / ESM variations)
      gsap = (gsapModule as any).gsap || (gsapModule as any).default || gsapModule;
      ScrollTrigger = (scrollTriggerModule as any).ScrollTrigger || (scrollTriggerModule as any).default || scrollTriggerModule;

      // register plugin once
      try {
        gsap.registerPlugin(ScrollTrigger);
      } catch (e) {
        // ignore if already registered
      }

      if (!sectionRef.current) return;

      // Use gsap.context for scoped selectors + cleanup
      ctx = gsap.context(() => {
        const items = gsap.utils.toArray(".about-item") as HTMLElement[];

        items.forEach((el: HTMLElement, i: number) => {
          gsap.fromTo(
            el,
            { y: 80, opacity: 0, rotateY: 8 },
            {
              y: 0,
              opacity: 1,
              rotateY: 0,
              duration: 0.7,
              ease: "power2.out",
              delay: i * 0.06,
              scrollTrigger: {
                trigger: el,
                start: "top 85%",
                toggleActions: "play none none reverse",
              },
            }
          );
        });
      }, sectionRef);
    })();

    return () => {
      cancelled = true;
      try {
        if (ctx) ctx.revert(); // kills timelines & removes inline styles
      } catch (e) {
        // ignore
      }
      // Note: ScrollTrigger cleanup handled by ctx.revert() for scoped triggers.
    };
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-20 px-4" aria-label="About section">
      <div className="max-w-6xl mx-auto">
        <div className="md:space-y-12 space-y-8">
          <div className="text-center items-center flex flex-col">
            <BlurText
              text="About me"
              delay={100}
              animateBy="words"
              direction="top"
              className="text-center bg-gradient-to-r from-cyan-600 to-purple-400 bg-clip-text text-transparent font-extrabold tracking-tighter lg:text-7xl md:text-5xl text-5xl justify-center"
            />

            <BlurText
              text="I tell computers to do things, sometimes they listen"
              delay={30}
              animateBy="words"
              direction="top"
              className="md:text-xl text-lg max-w-3xl mx-auto text-center flex justify-center bg-muted-foreground bg-clip-text"
            />
          </div>

          {/* NOTE: Use about-grid for container and about-item for each element */}
          <div className="grid lg:grid-cols-3 gap-8 items-start about-grid">
            {/* Stats Card (about-item) */}
            <div className="space-y-6 h-full about-item">
              <div className="glass-card md:p-6 p-5 text-center h-full">
                <img
                  src={profileData.profImg}
                  className="w-full h-full mx-auto mb-4 flex items-center justify-center text-4xl font-bold text-primary-foreground bg-cover object-cover object-center rounded-sm"
                  alt="Profile Image"
                />
              </div>
            </div>

            {/* Summary Card (about-item) */}
            <div className="lg:col-span-2 about-item">
              <div className="glass-card md:p-8 p-5 h-full">
                <h3 className="text-2xl font-semibold mb-6 text-foreground">My Journey</h3>
                <p className="text-muted-foreground leading-relaxed text-lg">
                  {profileData.summary}
                </p>

                <div className="mt-8 space-y-4">
                  <h4 className="text-lg font-medium text-foreground">What I'm Focused On</h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start">
                      <span className="text-primary mr-2">▶</span>
                      Building scalable full-stack applications with modern frameworks
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">▶</span>
                      Exploring AI/ML integration in web and mobile applications
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">▶</span>
                      Contributing to open-source projects and hackathon challenges
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div> {/* end about-grid */}
        </div>
      </div>
    </section>
  );
}
